import { servicePost } from "./client.ts";

/* -- Customer Event types -------------------------------- */
export interface EventProduct {
    Event: string;
    Name: string;
    SlotName: string;
    SlotID: number;
    ProductID: number;
    Quantity: number;
    Weight: number;
    ProductWeight: number;
    IsValid: boolean;
    Infected: boolean;
    MarkedAsUncertain: boolean;
    X: number;
    Y: number;
    Relevance: number;
    Extra: string;
    When: string;
    AccessLevel: number;
    TookInfection: boolean;
    WasInfected: boolean;
    HadSimilarWeightInHand: boolean;
    HadProductInHand: boolean;
    ProbableCustomers: number;
    CurrentCart: string;
    Seconds?: number;
    /** Client-side: an SNG cart line at payment that differs from the scans. */
    CartAtPayment?: boolean;
}

export interface CustomerEvent {
    KioskID: number;
    InstantID: string;
    CustomerReference: string;
    EditedCartManually: boolean;
    EnteredAtExit: boolean;
    MarkedInspection: boolean;
    Started: string;
    Ended: string;
    Email: string;
    Phone: string;
    EventList: EventProduct[];
    InspectionReason: string;
    IsVideoDownloaded: boolean;
    ResolvedBy: number;
    EnteredCount: number;
    TransactionID: number;
    PaymentLogID: number;
    CanRefund: boolean;
    CanCharge: boolean;
}

/** Server returns { "ChainValue": 5, "OtherChain": 12, ... } */
export function getCBValueUsage(sName: string) {
    return servicePost<Record<string, number>>("WebGetCBValueUsage", { sName });
}

export function addCBValue(column: string, displayText: string, filterValue: string, operatorFilter: string | null) {
    return servicePost("WebAddCBValue", { column, displayText, filterValue, operatorFilter });
}

export function deleteCBValue(sName: string, sDisplayText: string) {
    return servicePost<boolean>("WebDeleteCBValue", { sName, sDisplayText });
}

/** One card-check rule (server: `IDS.CardCheck`, table `S_Card_Check`).
 *  `ID` is negative for rules that come from the admin database rather than the
 *  tenant: those carry `OverrideType = "SUPER"` and are read-only here. */
export interface CardCheckItem {
    ID: number;
    /** Rule name. Also the merge key — a local rule shadows an admin one of the same name. */
    Name: string;
    /** Action: BLOCK | WHITE | OPEN. */
    Type: string;
    /** Pattern or literal value the rule matches on. */
    Content: string;
    /** What Content is compared against: NAME | BIN | HASH. */
    ContentType: string;
    /** Scope: BASE | GROUP | LOCATION | KIOSK (SUPER = admin-sourced, read-only). */
    OverrideType: string;
    /** Interpretation depends on OverrideType: `<GroupBy>|<value>` for GROUP,
     *  a location id for LOCATION, a kiosk id for KIOSK, blank for BASE. */
    OverrideKey: string;
    Deleted?: boolean;
    TS?: number;
}

/** Read one rule. The list comes from the `cardcheck` grid, not from here —
 *  there is no list endpoint. A negative id reads the admin-database copy. */
export function getCardCheckItem(id: number) {
    return servicePost<CardCheckItem>("GetCardCheckItem", { id });
}

export function saveCardCheckItem(item: CardCheckItem) {
    return servicePost<CardCheckItem>("SaveCardCheckItem", { item });
}

interface TransactionDetail {
    [key: string]: unknown;
}

export function getTransactionInformation(transactionID: string) {
    return servicePost<TransactionDetail>("TransactionInformation", { transactionID });
}

export function getTransactionHead(transactionID: string) {
    return servicePost<TransactionDetail>("TransactionHead", { transactionID });
}

export function getTransactionPaymentIDs(iTransactionID: string) {
    return servicePost<string[]>("WebGetTransactionPaymentIDs", { iTransactionID });
}

/** One rental disc/row on a transaction — surfaces due / delivered / returned
 *  dates so an admin can extend or manually mark items as returned. Shape
 *  mirrors IDS.TransactionDiscInformation on the server. */
export interface TransactionDiscRow {
    TransactionID: number;
    TransactionRowID: number;
    /** Transaction row type — 1 = rental, 45 = reserve (Access). Absent (0) on
     *  modules whose disc query doesn't select it. */
    TransactionTypeID?: number;
    Product: string;
    DueByDate: string | null;
    DeliverDate: string | null;
    ReturnDate: string | null;
}

/** Access reserve transaction-row type (rental is 1). */
export const RESERVE_ROW_TYPE = 45;

export function getTransactionDiscInfo(transactionId: number) {
    return servicePost<TransactionDiscRow[]>("TransactionDiscInfo", { transactionId });
}

/** Current UTC offset (minutes) of the kiosk's timezone — sourced server-side
 *  from dbo.Cache_TimezoneLookup. Used to render Due / Delivered / Returned
 *  dates in kiosk-local without an IANA lookup on the client. Returns 0 if
 *  the kiosk has no resolved timezone (caller can fall back to UTC). */
export function getKioskCurrentOffsetMinutes(kioskID: number) {
    return servicePost<number>("WebGetKioskCurrentOffsetMinutes", { kioskID });
}

/** Push the due-by date forward by `days` on the selected rows. Receipts
 *  already issued are NOT refunded by this — only the late-fee clock moves.
 *  Requires execTransactionReturnOps write. */
export function transactionExtendRows(transactionId: number, transactionRowId: number[], days: number) {
    return servicePost<boolean>("TransactionExtendRows", { transactionId, transactionRowId, days });
}

/** Manually flag the selected rows as returned at the given instant. Does
 *  NOT update product inventory state — only the transaction's processing
 *  view. Requires execTransactionReturnOps write. */
export function transactionSetRowReturnDate(transactionId: number, transactionRowId: number[], returnDate: string) {
    return servicePost<boolean>("TransactionSetRowReturnDate", { transactionId, transactionRowId, returnDate });
}

/** Set the whole reservation as returned: sets the reserve status to RETURN (via
 *  PayaP) and stamps the transaction head with an all-returned date. Transaction-
 *  level — there is no per-item reserve return. Requires execTransactionReturnOps write. */
export function returnReserveTransaction(instantId: string) {
    return servicePost<boolean>("WebReturnReserveRow", { instantId });
}

export interface TransactionRentalInfo {
    ChargeSold: boolean;
    ChargeLate: boolean;
    LatePeriodPrice: number;
    LateTaxRate: number;
    MaxPrice: number;
    MaxTax: number;
    RentalPeriod: number;
}

export interface TransactionChangeRow {
    RowID?: number;
    RowType: number;
    Product: number;
    ProductItemID?: number;
    InitialAmount?: number;
    InitialTax?: number;
    LateAmount?: number;
    LateTax?: number;
    Quantity?: number;
    Status: string;
    Sold: boolean;
    DeliverDate?: string;
    DueByDate?: string;
    ReturnDate?: string;
    Bias?: number;
    ReturnKiosk?: number;
    AdvancedTax?: string;
    RentalInfo?: TransactionRentalInfo | null;
}

export interface TransactionChangePayment {
    /** DVD_PaymentLog id. A REFUND row's RefundStatus names the id it refunds. */
    ID: number;
    /** sMethod; RETURN arrives as REFUND, with Price/Tax negated. */
    Type: string;
    CustomData?: string;
    ResultData: string;
    Price: number;
    Tax: number;
    RefundStatus?: number;
    /** SUBSIDY rows only: the package-deal id the subsidy is for. */
    SubsidyID?: number;
    PaymentDate?: string;
    Status?: string;
}

export interface TransactionChangeHead {
    KioskID: number;
    InstantID: string;
    TransactionID: number;
    InitialAmount: number;
    InitialTax: number;
    LateAmount: number;
    LateTax: number;
    Rows: TransactionChangeRow[];
    Payments: TransactionChangePayment[];
}

export interface UpdateTransactionContainer {
    Head: TransactionChangeHead;
    Verification: string;
    Issues: string[];
    IssuesAcknowleded?: string;
    Action: string;
    Comment: string;
    /** Payment-log ids a refund comes from, in order. Read by the server only for Action "refund". */
    RefundPaymentOrder?: number[];
    KioskID?: number;
}

export function getEditTransaction(instantid: string) {
    return servicePost<UpdateTransactionContainer>("GetUpdateTransactionInformation", { instantid });
}

/* Strip null / undefined / empty-string / empty-array values from an
 * `UpdateTransactionContainer` before sending. The server's
 * `UpdateTransactionInformation` (Transactions.vb:423) does
 * `String.Join(vbCrLf, con.Issues)` without a null-guard, so any payload
 * carrying `"Issues": null` crashes server-side. Same logic for other
 * fields that are conceptually "absent when empty" (KioskID,
 * IssuesAcknowleded, …). Nested objects (Head, Rows) are left alone — the
 * server expects their structure intact. */
function pruneContainer(con: UpdateTransactionContainer): Partial<UpdateTransactionContainer> {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(con)) {
        if (v === null || v === undefined) continue;
        if (typeof v === "string" && v.length === 0) continue;
        if (Array.isArray(v) && v.length === 0) continue;
        out[k] = v;
    }
    return out as Partial<UpdateTransactionContainer>;
}

export function saveEditTransaction(con: UpdateTransactionContainer) {
    return servicePost<UpdateTransactionContainer>("UpdateTransactionInformation", { con: pruneContainer(con) });
}

export function getCustomerEvent(instantID: string) {
    return servicePost<CustomerEvent>("WebGetCustomerEvent", { instantID });
}

/** One session in a rental chain: the rental (`IsReturn` false) or a return
 *  whose S_Customer_Event.RelationID names it. Server: CustomerEventRelation. */
export interface CustomerEventRelation {
    KioskID: number;
    InstantID: string;
    IsReturn: boolean;
    Started: string;
    Ended: string;
    /** Identity-camera InstantID, or "" when the session has no CAMERA event. */
    CamIdent: string;
}

/** The rental plus all its returns, from either side. Empty when there is no chain. */
export function getCustomerEventReturns(instantID: string) {
    return servicePost<CustomerEventRelation[]>("WebGetCustomerEventReturns", { instantID });
}

export function updateCustomerEventStatus(instantID: string, val: boolean, reason: string) {
    return servicePost("WebUpdateCustomerEventStatus", { instantID, val, reason });
}

export function getCustomerCartEdits(instantID: string) {
    return servicePost<unknown[]>("WebGetCustomerCartEdits", { instantID });
}

export function updateCustomerCart(eventID: string, items: unknown[]) {
    return servicePost("WebUpdateCustomerCart", { instantID: eventID, data: items });
}

/** Audit note (server: `IDS.Note`, table `S_Admin_Notes`). `AdminUserID` is always
 *  overwritten server-side with the calling user, so callers leave it off.
 *  `LastUpdated` is a non-nullable DateTime on the server — send it. */
export interface AdminNote {
    NoteID?: number;
    ProductID?: number | null;
    CustomerID?: number | null;
    TransactionID?: number | null;
    KioskID?: number | null;
    Note: string;
    Entered?: string;
    LastUpdated?: string;
    CustomerIssueType?: number | null;
    Open?: boolean;
    CustomerIssueID?: number | null;
    NoteType?: number;
    Command?: string;
}

/** Charge an additional amount on an already-settled transaction.
 *  `price` and `tax` are separate money amounts — the reason text is not a
 *  parameter here; record it with {@link saveNote} or an external detail. */
export function extraChargeTransaction(transactionID: number, price: number, tax: number) {
    return servicePost<boolean>("WebExtraChargeTransaction", { transID: transactionID, price, tax });
}

/** Refund one payment. The refund targets a single row of `DVD_PaymentLog`, so
 *  `paymentLogID` is required — the server refuses an id that names no log, and
 *  amount/tax are each clamped to what that log actually took.
 *  `note` is written to `S_Admin_Notes` with the payment reference appended. */
export function refundTransaction(
    transactionID: number, paymentLogID: number, amount: number, tax: number, note: AdminNote,
) {
    return servicePost<boolean>("WebRefundTransaction", {
        iTransactionID: transactionID,
        iPaymentLogID: paymentLogID,
        dAmount: amount,
        dTax: tax,
        note,
    });
}

export function queueEmailTemplate(args: { EmailTemplateID: number; Address: string; PrimaryID: number; SecondaryID: number }) {
    return servicePost<boolean>("WebQueueEmailTemplate", args);
}

export function saveNote(note: {
    TransactionID?: number;
    CustomerID?: number;
    KioskID?: number;
    ProductID?: number;
    Note: string;
    CustomerIssueType?: number;
    CustomerIssueID?: number;
}) {
    return servicePost<boolean>("WebSaveNote", { note });
}

export function ignoreTransaction(transactionID: number, ignore: boolean) {
    return servicePost<boolean>("WebIgnoreTransactions", { transactionID, ignore });
}

export function stopCharging(transactionId: number, isInitial: boolean, setStop: boolean, reason: string | null) {
    return servicePost<Record<string, unknown>>("TransactionStopCharging", { transactionId, isInitial, setStop, reason });
}

export interface TransactionStats {
    TransactionID: number;
    InstantID: string;
    StopChargingInitialFee: boolean;
    StopChargingInitialFeeReason: string;
    StopChargingLateFee: boolean;
    StopChargingLateFeeReason: string;
    Ignore: boolean;
}

export function getTransactionStats(transactionId: number, signal?: AbortSignal) {
    return servicePost<TransactionStats>("WebGetTransactionStats", { transactionId }, signal);
}

export interface FetchedPromotionCode {
    PromotionCodeID: number;
    Code: string;
    FromDate: string;
    ToDate: string;
}

export function assignPromotions(
    promotionID: number, codes: number, validTo: string,
    transactionID: number, customerID: number, kioskID: number,
) {
    return servicePost<FetchedPromotionCode[]>("WebGetPromotionCodesAndAddNote", {
        promotionID, codes, validTo, transactionID, customerID, kioskID,
    });
}

export function sendKioskCommandVideo(kioskID: number, command: string, iData: number, sData: string) {
    return servicePost<boolean>("WebSendKioskCommandVideo", { kioskID, command, iData, sData });
}

/** URL of a transaction's kiosk recording, as the transaction editor opens it.
 *  Recordings live in a folder per endpoint (the endpoint title, lower-cased;
 *  SmartAccess is stored under "automatedx"), then per kiosk, named by InstantID. */
export function kioskVideoUrl(endpointTitle: string | null | undefined, kioskID: number, instantID: string): string {
    const name = String(endpointTitle ?? "").toLowerCase();
    const folder = name === "smartaccess" ? "automatedx" : name;
    return `http://kioskvideos.partner.instantsystems.se/${folder}/${kioskID}/${encodeURIComponent(instantID)}.avi`;
}

export function getVideoStorageName() {
    return servicePost<string>("WebGetVideoStorageName", {});
}

export function getKioskVideos(instantID: string) {
    return servicePost<string[]>("WebGetKioskVideos", { InstantID: instantID });
}

/** Resolved price for one PriceType + the specificity Weight of the override
 *  that produced it (so callers can see which scope the price came from).
 *  `Value` is omitted when there is NO price for the type (NULL); a present
 *  `Value` — including 0 — is an explicit price. */
export interface ResolvedPrice {
    Value?: number;
    Weight: number;
}

/**
 * Server returns `Dictionary<int, PriceTypeResult>` — every price/tax type
 * (S_Generic_Price_Type ids 1-9, 11-15) resolved through the hierarchy for the
 * kiosk, each with its winning override's Weight. System.Text.Json serializes
 * this as an object with stringified numeric keys:
 * `{"1": {"Value": 2.49, "Weight": 136}, "2": {...}}`. Read by key, not index.
 */
export function getProductKioskPrice(productID: number, kioskID: number) {
    return servicePost<Record<string, ResolvedPrice>>("GetProductKioskPrice", { productID, kioskID });
}

// ── SuperAdmin: kiosk-by-serial lookup ───────────────────────────────────────

export interface SerialMatch {
    EndpointID: number;
    EndpointTitle?: string;
    KioskID: number;
    Serial?: string;
    StoreName?: string;
    Country?: string;
    OperatorID?: number | null;
    Status?: string;
    RemovalDate?: string | null;
    KioskType?: string;
    ExternalReference?: string;
    /** Latest heartbeat from PayaP_Kiosk_Activity. Both NULL when the kiosk
     *  has never reported (provisioned but not yet deployed). */
    LastActivity?: string | null;
    LastStatus?: string | null;
}

export interface SerialKioskComponent {
    ID?: number;
    KioskSerialNumber?: string;
    ComponentID?: number;
    ComponentName?: string;
    ComponentSerial?: string;
    PositionNumber?: number;
    ScannedDate?: string;
    EarliestDate?: string | null;
}

export interface SerialLookupResult {
    Serial: string;
    Matches: SerialMatch[];
    Components: SerialKioskComponent[];
}

/** SuperAdmin-only. Resolves a kiosk serial → endpoint + component scans.
 *  Authoritative source is PayaP_Global.PayaP_Kiosk (carries both endpointId
 *  and sSerialNumber); InstantInventory.Approval_Kiosk_Component_Scanned
 *  feeds the components panel. Permission-gated server-side via
 *  `<MethodSuperAdminPermission>`. */
export function superAdminLookupKioskBySerial(serial: string) {
    return servicePost<SerialLookupResult>("WebSuperAdminLookupKioskBySerial", { serial });
}

export function editCartGetPromotionCode(code: string) {
    return servicePost<{ Code: string; PromotionID: number; PromotionCodeID: number }>("EditCartGetPromotionCode", { code });
}
